// apps/frontend/src/hooks/useNotifications.ts
// 🔔 Notifications temps réel via WebSocket

import { useState, useEffect, useCallback, useRef } from 'react';
import wsService, {
  type NotificationPayload,
} from '../services/websocketService';
import { useAuth } from './useAuth';

export type { NotificationPayload };

interface UseNotificationsReturn {
  notifications: NotificationPayload[];
  unreadCount: number;
  isLoading: boolean;
  markRead: (id: string) => void;
  markAllRead: () => void;
  fetchMore: () => void;
  hasMore: boolean;
}

const PAGE_SIZE = 20;

export function useNotifications(): UseNotificationsReturn {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<NotificationPayload[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const subscribedRef = useRef(false);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Unread count derived from state
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const setupListeners = useCallback(() => {
    // New real-time notification pushed by server
    const offNew = wsService.onNewNotification(notification => {
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n.id === notification.id)) return prev;
        return [notification, ...prev];
      });
    });

    // Batch fetch response
    const offReceived = wsService.onNotificationsReceived(
      ({ notifications: list, hasMore: more }) => {
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        setNotifications(prev => {
          if (offsetRef.current === 0) return list;
          const existingIds = new Set(prev.map(n => n.id));
          return [...prev, ...list.filter(n => !existingIds.has(n.id))];
        });
        setHasMore(more);
        setIsLoading(false);
      }
    );

    // Single mark-read confirmed
    const offRead = wsService.onNotificationReadConfirmed(
      ({ notificationId }) => {
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, isRead: true } : n))
        );
      }
    );

    // Mark-all-read confirmed
    const offAllRead = wsService.onAllNotificationsReadConfirmed(() => {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    });

    return () => {
      offNew?.();
      offReceived?.();
      offRead?.();
      offAllRead?.();
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || subscribedRef.current) return;

    // Wait until socket is connected (it may already be from chat)
    const trySubscribe = () => {
      if (!wsService.isConnected) return;
      subscribedRef.current = true;
      wsService.subscribeToNotifications();
      setIsLoading(true);
      offsetRef.current = 0;
      wsService.fetchNotifications({ limit: PAGE_SIZE, offset: 0 });
      // Fallback : si pas de réponse en 5s, arrêter le spinner
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = setTimeout(() => setIsLoading(false), 5000);
    };

    // If already connected, subscribe immediately
    if (wsService.isConnected) {
      trySubscribe();
    }

    // Otherwise wait for connection
    const offConn = wsService.onConnection(connected => {
      if (connected && !subscribedRef.current) trySubscribe();
    });

    const offListeners = setupListeners();

    return () => {
      offConn();
      offListeners();
      subscribedRef.current = false;
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      setIsLoading(false);
    };
  }, [isAuthenticated, setupListeners]);

  const markRead = useCallback((id: string) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
    );
    wsService.markNotificationRead(id);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    wsService.markAllNotificationsRead();
  }, []);

  const fetchMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    offsetRef.current += PAGE_SIZE;
    setIsLoading(true);
    wsService.fetchNotifications({
      limit: PAGE_SIZE,
      offset: offsetRef.current,
    });
  }, [isLoading, hasMore]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    fetchMore,
    hasMore,
  };
}

export default useNotifications;
